import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  Card,
  CardContent,
  IconButton,
  Autocomplete,
  Avatar,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const EmployerKYC = ({ step, formData, updateFormData, onNext, onBack, loading }) => {
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [documents, setDocuments] = useState({
    businessLicense: formData.businessLicense || [],
    companyRegistration: formData.companyRegistration || [],
    taxCertificate: formData.taxCertificate || [],
    otherDocuments: formData.otherDocuments || []
  });

  // Predefined options
  const industryTypes = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing',
    'Retail', 'Construction', 'Agriculture', 'Mining', 'Tourism',
    'Transportation', 'Telecommunications', 'Energy', 'Government',
    'Non-Profit', 'Consulting', 'Media', 'Real Estate', 'Other'
  ];
  
  const companySizes = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '501-1000 employees',
    '1000+ employees'
  ];
  
  const jobCategories = [
    'Software Development', 'Data Science', 'Marketing', 'Sales', 'HR',
    'Finance', 'Operations', 'Customer Service', 'Design', 'Engineering',
    'Healthcare', 'Education', 'Legal', 'Administration', 'Management'
  ];

  const getValidationSchema = () => {
    switch (step) {
      case 'company':
        return Yup.object({
          companyName: Yup.string().required('Company name is required'),
          industryType: Yup.string().required('Industry type is required'),
          companySize: Yup.string().required('Company size is required'),
          companyRegNumber: Yup.string().required('Company registration number is required'),
          officeAddress: Yup.string().required('Office address is required'),
          companyPhone: Yup.string().required('Company phone is required'),
          companyEmail: Yup.string().email('Invalid email').required('Company email is required')
        });
      case 'representative':
        return Yup.object({
          representativeName: Yup.string().required('Representative name is required'),
          representativePosition: Yup.string().required('Position is required'),
          representativePhone: Yup.string().required('Phone number is required'),
          representativeEmail: Yup.string().email('Invalid email').required('Email is required')
        });
      case 'documents':
        return Yup.object({
          businessLicense: Yup.array().min(1, 'Business license is required'),
          companyRegistration: Yup.array().min(1, 'Company registration document is required')
        });
      default:
        return Yup.object({});
    }
  };

  const formik = useFormik({
    initialValues: {
      companyName: formData.companyName || '',
      industryType: formData.industryType || '',
      companySize: formData.companySize || '',
      companyRegNumber: formData.companyRegNumber || '',
      officeAddress: formData.officeAddress || '',
      companyPhone: formData.companyPhone || '',
      companyEmail: formData.companyEmail || '',
      website: formData.website || '',
      representativeName: formData.representativeName || '',
      representativePosition: formData.representativePosition || '',
      representativePhone: formData.representativePhone || '',
      representativeEmail: formData.representativeEmail || '',
      jobCategories: formData.jobCategories || [],
      recruitmentTeam: formData.recruitmentTeam || []
    },
    validationSchema: getValidationSchema(),
    onSubmit: (values) => {
      setSubmitError('');
      const updatedData = { ...values, ...documents };
      updateFormData(updatedData);
      onNext();
    },
    enableReinitialize: true
  });

  // Document upload handlers
  const handleFileUpload = (event, documentType) => {
    const files = Array.from(event.target.files);
    
    files.forEach(file => {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please upload only PDF, JPEG, or PNG files');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const newDocument = {
          id: Date.now() + Math.random(),
          name: file.name,
          type: file.type,
          size: file.size,
          preview: e.target.result,
          file: file
        };
        
        setDocuments(prev => ({
          ...prev,
          [documentType]: [...prev[documentType], newDocument]
        }));
      };
      reader.readAsDataURL(file);
    });
    
    // Clear the input
    event.target.value = '';
  };

  const removeDocument = (documentType, documentId) => {
    setDocuments(prev => ({
      ...prev,
      [documentType]: prev[documentType].filter(doc => doc.id !== documentId)
    }));
  };

  const addDocumentSlot = (documentType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.onchange = (e) => handleFileUpload(e, documentType);
    input.click();
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
      updateFormData({ companyLogo: file });
    }
  };

  const addTeamMember = () => {
    const newMember = {
      name: '',
      position: '',
      email: '',
      phone: ''
    };
    formik.setFieldValue('recruitmentTeam', [...formik.values.recruitmentTeam, newMember]);
  };

  const removeTeamMember = (index) => {
    const updatedTeam = formik.values.recruitmentTeam.filter((_, i) => i !== index);
    formik.setFieldValue('recruitmentTeam', updatedTeam);
  };

  const renderCompanyDetails = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Company Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Provide information about your company
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="companyName"
            label="Company Name"
            value={formik.values.companyName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.companyName && Boolean(formik.errors.companyName)}
            helperText={formik.touched.companyName && formik.errors.companyName}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={formik.touched.industryType && Boolean(formik.errors.industryType)}>
            <InputLabel>Industry Type</InputLabel>
            <Select
              name="industryType"
              value={formik.values.industryType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Industry Type"
            >
              {industryTypes.map((industry) => (
                <MenuItem key={industry} value={industry}>{industry}</MenuItem>
              ))}
            </Select>
            {formik.touched.industryType && formik.errors.industryType && (
              <FormHelperText>{formik.errors.industryType}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={formik.touched.companySize && Boolean(formik.errors.companySize)}>
            <InputLabel>Company Size</InputLabel>
            <Select
              name="companySize"
              value={formik.values.companySize}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Company Size"
            >
              {companySizes.map((size) => (
                <MenuItem key={size} value={size}>{size}</MenuItem>
              ))}
            </Select>
            {formik.touched.companySize && formik.errors.companySize && (
              <FormHelperText>{formik.errors.companySize}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="companyRegNumber"
            label="Company Registration Number"
            value={formik.values.companyRegNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.companyRegNumber && Boolean(formik.errors.companyRegNumber)}
            helperText={formik.touched.companyRegNumber && formik.errors.companyRegNumber}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="officeAddress"
            label="Office Address"
            multiline
            rows={2}
            value={formik.values.officeAddress}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.officeAddress && Boolean(formik.errors.officeAddress)}
            helperText={formik.touched.officeAddress && formik.errors.officeAddress}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            name="companyPhone"
            label="Company Phone"
            value={formik.values.companyPhone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.companyPhone && Boolean(formik.errors.companyPhone)}
            helperText={formik.touched.companyPhone && formik.errors.companyPhone}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            name="companyEmail"
            label="Company Email"
            type="email"
            value={formik.values.companyEmail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.companyEmail && Boolean(formik.errors.companyEmail)}
            helperText={formik.touched.companyEmail && formik.errors.companyEmail}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="website"
            label="Website (Optional)"
            placeholder="https://www.example.com"
            value={formik.values.website}
            onChange={formik.handleChange}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={jobCategories}
            value={formik.values.jobCategories}
            onChange={(event, newValue) => {
              formik.setFieldValue('jobCategories', newValue);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const tagProps = getTagProps({ index });
                const { key, ...rest } = tagProps;
                return (
                  <Chip
                    variant="outlined"
                    label={option}
                    key={key}
                    {...rest}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Job Categories You Hire For"
                placeholder="Select categories"
              />
            )}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="logo-upload"
              type="file"
              onChange={handleLogoUpload}
            />
            <label htmlFor="logo-upload">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                Upload Company Logo
              </Button>
            </label>
            {logoPreview && (
              <Box sx={{ mt: 2 }}>
                <Avatar 
                  src={logoPreview} 
                  sx={{ width: 100, height: 100, mx: 'auto' }}
                  variant="rounded"
                >
                  <BusinessIcon />
                </Avatar>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  const renderRepresentativeInfo = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Representative Information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Details of the person representing the company
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            name="representativeName"
            label="Representative Name"
            value={formik.values.representativeName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.representativeName && Boolean(formik.errors.representativeName)}
            helperText={formik.touched.representativeName && formik.errors.representativeName}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            name="representativePosition"
            label="Position/Title"
            value={formik.values.representativePosition}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.representativePosition && Boolean(formik.errors.representativePosition)}
            helperText={formik.touched.representativePosition && formik.errors.representativePosition}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            name="representativePhone"
            label="Phone Number"
            value={formik.values.representativePhone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.representativePhone && Boolean(formik.errors.representativePhone)}
            helperText={formik.touched.representativePhone && formik.errors.representativePhone}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            name="representativeEmail"
            label="Email Address"
            type="email"
            value={formik.values.representativeEmail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.representativeEmail && Boolean(formik.errors.representativeEmail)}
            helperText={formik.touched.representativeEmail && formik.errors.representativeEmail}
          />
        </Grid>
      </Grid>

      {/* Recruitment Team Section */}
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
        Recruitment Team (Optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add other team members who will be involved in recruitment
      </Typography>
      
      {formik.values.recruitmentTeam.map((member, index) => (
        <Card key={index} sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name={`recruitmentTeam[${index}].name`}
                  label="Name"
                  value={member.name}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  name={`recruitmentTeam[${index}].position`}
                  label="Position"
                  value={member.position}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={1}>
                <IconButton
                  color="error"
                  onClick={() => removeTeamMember(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name={`recruitmentTeam[${index}].email`}
                  label="Email"
                  type="email"
                  value={member.email}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name={`recruitmentTeam[${index}].phone`}
                  label="Phone"
                  value={member.phone}
                  onChange={formik.handleChange}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
      
      <Button
        startIcon={<AddIcon />}
        onClick={addTeamMember}
        sx={{ mb: 2 }}
      >
        Add Team Member
      </Button>
    </Box>
  );

  const renderDocumentUpload = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Company Documents
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload required company documents for verification
      </Typography>

      <Grid container spacing={3}>
        {/* Business License */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Business License *
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload your business license document
              </Typography>
              
              {documents.businessLicense.map((doc, index) => (
                <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {doc.name} ({(doc.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                  <IconButton size="small" onClick={() => removeDocument('businessLicense', doc.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => addDocumentSlot('businessLicense')}
                sx={{ mt: 1 }}
              >
                Add Business License
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Company Registration */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Company Registration Certificate *
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload your company registration certificate
              </Typography>
              
              {documents.companyRegistration.map((doc, index) => (
                <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {doc.name} ({(doc.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                  <IconButton size="small" onClick={() => removeDocument('companyRegistration', doc.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => addDocumentSlot('companyRegistration')}
                sx={{ mt: 1 }}
              >
                Add Registration Certificate
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Tax Certificate */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Tax Certificate
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload your tax certificate (optional)
              </Typography>
              
              {documents.taxCertificate.map((doc, index) => (
                <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {doc.name} ({(doc.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                  <IconButton size="small" onClick={() => removeDocument('taxCertificate', doc.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => addDocumentSlot('taxCertificate')}
                sx={{ mt: 1 }}
              >
                Add Tax Certificate
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Other Documents */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Other Documents
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload any additional company documents
              </Typography>
              
              {documents.otherDocuments.map((doc, index) => (
                <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {doc.name} ({(doc.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                  <IconButton size="small" onClick={() => removeDocument('otherDocuments', doc.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => addDocumentSlot('otherDocuments')}
                sx={{ mt: 1 }}
              >
                Add Other Documents
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderStepContent = () => {
    switch (step) {
      case 'company':
        return renderCompanyDetails();
      case 'representative':
        return renderRepresentativeInfo();
      case 'documents':
        return renderDocumentUpload();
      default:
        return renderCompanyDetails();
    }
  };

  const handleNext = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors || {}).length > 0) {
      const touched = {};
      Object.keys(errors).forEach((k) => { touched[k] = true; });
      formik.setTouched(touched, true);
      const firstErrKey = Object.keys(errors)[0];
      const firstErrMsg = typeof errors[firstErrKey] === 'string' ? errors[firstErrKey] : 'Please complete the required fields';
      setSubmitError(firstErrMsg);
      return;
    }
    setSubmitError('');
    formik.handleSubmit();
  };

  const getStepTitle = () => {
    switch (step) {
      case 'company':
        return 'Company Information';
      case 'representative':
        return 'Representative Details';
      case 'documents':
        return 'Company Documents';
      default:
        return 'Company Information';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'company':
        return 'Tell us about your company and business details';
      case 'representative':
        return 'Provide information about your company representative';
      case 'documents':
        return 'Upload required company documents for verification';
      default:
        return 'Complete your company profile';
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h5" component="h2" gutterBottom color="primary">
          {getStepTitle()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {getStepDescription()}
        </Typography>
        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
      </Box>

      {renderStepContent()}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button 
          onClick={onBack} 
          disabled={loading}
          variant="outlined"
          size="large"
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={loading}
          size="large"
          sx={{ minWidth: 120 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            step === 'documents' ? 'Complete Profile' : 'Continue'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default EmployerKYC;